import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { cloneDeep } from 'es-toolkit'
import { findIndex, includes } from 'es-toolkit/compat'
import type { IconType } from 'react-icons'
import { LuGlobe2, LuMicroscope } from 'react-icons/lu'
import { StateCreator, createStore } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import type { ChatSessionListQueryKeyArgs } from '@/components/chat-v2/hooks/chatSessionQueryKeys'
import type { ChatSessionListItem } from '@/components/chat-v2/hooks/useChatSession'
import type { MakeSendMessageParamsOptions } from '@/components/chat-v2/hooks/useMessageHandlers'
import { withoutComposerDraftParams } from '@/components/chat-v2/lib/export'
import {
  type EntrypointId,
  type PanelViewerState,
  type PanelTab,
  type TabNavState,
  createListNavState,
  createPanelTab,
  getViewportContextFromPanel,
  stripViewportContextSelection,
  isEntrypointId,
  getFilesEntrypointForAgent,
} from '@/components/chat-v2/lib/panelEntrypoints'
import { createPastedTextCardTitle } from '@/components/chat-v2/lib/pastedText'
import { readLatestViewerState } from '@/components/chat-v2/lib/viewerStateBridge'

import { IS_FEDRAMP_ENVIRONMENT } from '@/constants/env'
import {
  AUTO_MODEL_SELECTOR,
  MODEL_SELECTOR_OPTIONS,
  type ModelOption,
  VENDOR_MODEL_SELECTORS,
  vendorOfOption,
} from '@/constants/modelSelectors'

import type {
  AgentName,
  FileNamespaceValue,
  KnownModelName,
  WebSearchMethod,
  WithFocusFiles,
  WithSelectedConnectorSources,
  WithSelectedMcpServers,
  WithViewportContext,
  WithWebSearchAnswerTool,
} from '@/types/chat-v2'
import type { WithOverrideModelName } from '@/types/chat-v2/sessions'
import type { FileType, File as ServerFile } from '@/types/File'
import type { UUID } from '@/types/Id'
import type { PlaybookScope } from '@/types/library/PlaybookDefinition'
import {
  type PlaysCitation,
  playsCitationKey,
} from '@/types/library/PlaybookRef'

// MARK: tool types

export type ToolType =
  | 'web_search'
  | 'deep_research'
  | 'search_organization_files'
  | 'search_opportunity_files'
  | 'search_pursuit_files'
  | 'search_files'
  | 'text_search'
  | 'usaspending'

export interface Tool {
  id: ToolType
  name: string
  description: string
  icon: IconType
  cuiAllowed: boolean
  placeholder?: string
}

export const AVAILABLE_TOOLS: Tool[] = [
  {
    id: 'web_search',
    name: 'Web Search',
    description: 'Search the internet for real-time information',
    icon: LuGlobe2,
    placeholder:
      "Ask me anything and I'll search the web for the latest information...",
    cuiAllowed: false,
  },
  {
    id: 'deep_research',
    name: 'Deep Research',
    description: 'Conduct comprehensive, multi-step research on the web',
    icon: LuMicroscope,
    placeholder: 'Get a detailed research report...',
    cuiAllowed: false,
  },
]

// MARK: chat config store provider

interface PendingAttachment {
  status: 'pending'
  id: UUID
  type: FileType
  file: File
}

interface CompleteAttachment {
  status: 'complete'
  id: UUID
  type: FileType
  file: ServerFile
}

interface FileSourceViewerConfig {
  kind: 'file'
  id: UUID
  type: FileType
}

interface PlaybookSourceViewerConfig {
  kind: 'playbook'
  id: string
  excerptId: UUID | null
  name: string
  content: string
  scope: PlaybookScope
}

export type SourceViewerConfig =
  | FileSourceViewerConfig
  | PlaybookSourceViewerConfig

export type Attachment = PendingAttachment | CompleteAttachment

// Image attachments are sent as ImageUrl content in the message (not as focus files)
export interface QueuedImageAttachment {
  id: UUID
  status: 'queued'
  file: File
  previewUrl: string
  fileName: string
  contentType: string
}

export interface PendingImageAttachment {
  id: UUID
  status: 'pending'
  previewUrl: string
  fileName: string
  contentType: string
}

export interface CompleteImageAttachment {
  id: UUID
  status: 'complete'
  previewUrl: string
  s3Uri: string
  fileName: string
  contentType: string
}

export type ImageAttachment =
  | QueuedImageAttachment
  | PendingImageAttachment
  | CompleteImageAttachment

export type PastedTextCardSource = 'paste' | 'playbook' | 'selection' | 'reply'

export interface PastedTextCard {
  id: UUID
  source: PastedTextCardSource
  title: string
  text: string
  createdAt: string
  location?: {
    fileId: UUID
    fileName: string
    namespace?: FileNamespaceValue
    path?: string
    /** 1-based PDF page or presentation slide number. */
    page?: number
    /**
     * Selection locator (selectionLocator.ts DSL — superset of the canonical
     * markit anchors). One string per card: chat-only forms (`pdf:bbox(...)`,
     * `txt:plain`) restore the exact selection on reopen, and are downgraded
     * to their canonical citation form when serialized into the sent message
     * (see pastedText.ts).
     */
    locator?: string
  }
  /** A selection of plays on a playbook's map, not of a file. */
  plays?: PlaysCitation
}

/** A card by the plays it cites, so the same play isn't staged twice. */
const playsCardKey = (card: PastedTextCard) =>
  card.plays ? playsCitationKey(card.plays) : card.id

export type SourceViewerMode = 'dialog' | 'panel'
export type SourceViewerState = 'hidden' | 'open'

export interface DocumentVersionReview {
  changed: string | null
  confirmationOwner: string | null
  regenerated: boolean
}

export interface ChatEnvironmentStoreState {
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void

  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void

  modelOptions: ModelOption[]
  selectedModel: ModelOption
  setSelectedModel: (modelName: KnownModelName | string) => void

  selectedTools: ToolType[]
  setSelectedTools: (tools: ToolType[]) => void
  addTool: (tool: ToolType) => void
  removeTool: (tool: ToolType) => void

  // MCP server integrations selected for this conversation
  selectedIntegrations: string[]
  setSelectedIntegrations: (integrations: string[]) => void
  addIntegration: (integrationId: string) => void
  removeIntegration: (integrationId: string) => void

  // Connector-backed data sources (e.g. GovWin) selected for this conversation
  selectedConnectorSources: string[]
  setSelectedConnectorSources: (sources: string[]) => void
  addConnectorSource: (source: string) => void
  removeConnectorSource: (source: string) => void

  searchQuery: string
  setSearchQuery: (query: string) => void

  expandedMarkdownTableKey: string | null
  setExpandedMarkdownTableKey: (
    key: string | null | ((prev: string | null) => string | null),
  ) => void

  // Ephemeral disclosure state survives tool remounts within this chat.
  toolCardExpansion: Record<string, boolean | undefined>
  setToolCardExpanded: (args: { key: string; expanded: boolean }) => void

  documentVersionSurfaces: Record<string, readonly string[] | undefined>
  registerDocumentVersionSurface: (args: {
    draftId: string
    surfaceId: string
  }) => () => void

  documentVersionReviews: Record<string, DocumentVersionReview | undefined>
  updateDocumentVersionReview: (args: {
    draftId: string
    update: Partial<DocumentVersionReview>
  }) => void

  selectedSessionIds: UUID[]
  setSelectedSessionIds: (ids: UUID[]) => void
  setSessionSelected: (id: UUID, selected: boolean) => void
  clearSessionSelection: () => void

  submitPending: boolean
  setSubmitPending: (submitPending: boolean) => void

  submitDisabled: boolean
  setSubmitDisabled: (submitDisabled: boolean) => void
  chatContinuationRequestMessageId: string | null
  setChatContinuationRequestMessageId: (
    messageId:
      | string
      | null
      | ((previousMessageId: string | null) => string | null),
  ) => void

  activeSourceId: number | string | null
  setActiveSourceId: (
    sourceId:
      | number
      | string
      | null
      | ((previousSourceId: number | string | null) => number | string | null),
  ) => void

  attachments: Attachment[]
  addAttachment: (attachment: Attachment) => void
  removeAttachment: (attachmentId: UUID) => void
  clearAttachments: () => void
  setAttachments: (attachments: Attachment[]) => void
  convertPendingToComplete: (attachmentId: UUID, file: ServerFile) => void

  // Image attachments (sent as ImageUrl content in messages, not as focus files)
  imageAttachments: ImageAttachment[]
  getImageAttachments: () => ImageAttachment[]
  addImageAttachment: (attachment: ImageAttachment) => void
  updateImageAttachment: (
    attachmentId: UUID,
    update: Partial<ImageAttachment>,
  ) => void
  removeImageAttachment: (attachmentId: UUID) => void
  clearImageAttachments: () => void
  setImageAttachments: (attachments: ImageAttachment[]) => void

  pastedTextCards: PastedTextCard[]
  getPastedTextCards: () => PastedTextCard[]
  addPastedTextCard: (card: PastedTextCard) => void
  /** Stages cards of picked plays, each play once: one already staged isn't added again. */
  addPlaysCards: (cards: PastedTextCard[]) => void
  updatePastedTextCard: (cardId: UUID, text: string) => void
  removePastedTextCard: (cardId: UUID) => void
  clearPastedTextCards: () => void
  setPastedTextCards: (cards: PastedTextCard[]) => void

  sourceViewerState: SourceViewerState
  setSourceViewerState: (
    state:
      | SourceViewerState
      | ((state: SourceViewerState) => SourceViewerState),
  ) => void
  viewerStateByTabId: Record<string, PanelViewerState>
  setViewerStateByTabId: (tabId: string, state: PanelViewerState | null) => void
  /** The selection chip whose capture is currently lit in the viewer: armed
   * when a chip opens (keyed by that open's locator stamp), taken down when
   * the viewer dismisses the highlight. The signature names the chip. */
  activeSelectionChip: { key: number; signature: string } | null
  setActiveSelectionChip: (
    chip: { key: number; signature: string } | null,
  ) => void
  /** Clears only the chip the dismissed reveal armed: an older reveal going
   * down must not darken a newer chip. */
  dismissActiveSelectionChip: (key: number) => void
  /** Bumped when add-to-chat consumes the viewer selection; office viewers
   * watch it to drop their in-viewer selection overlays. */
  viewerSelectionResetNonce: number
  bumpViewerSelectionResetNonce: () => void
  /** Tabs whose selection a send consumed while they were in the
   * background. Their viewer is not mounted to see the reset nonce, and
   * an office viewer's retained store re-reports the range when it comes
   * back, so the reset waits for the tab to be shown again. */
  staleSelectionTabIds: readonly string[]
  markSelectionsStale: (tabIds: readonly string[]) => void
  /** Whether ``tabId`` was marked stale; unmarks it. */
  takeStaleSelection: (tabId: string) => boolean
  /** Bumped when the add-selection shortcut (⌘L / Ctrl+Shift+L) lands a
   * card; the composer textarea watches it and grabs keyboard focus so the
   * user can start typing immediately. */
  composerFocusNonce: number
  requestComposerFocus: () => void
  sessionHistoryOpen: boolean
  setSessionHistoryOpen: (open: boolean) => void

  fullScreenViewerOpen: boolean
  setFullScreenViewerOpen: (open: boolean) => void
  fullScreenViewerFileId: UUID | null
  setFullScreenViewerFileId: (fileId: UUID | null) => void
  openFullScreenViewer: (fileId: UUID) => void
  closeFullScreenViewer: () => void

  /** Workspace panel expanded over the whole chat surface (any tab). */
  panelExpanded: boolean
  setPanelExpanded: (expanded: boolean) => void

  isRecording: boolean
  setIsRecording: (isRecording: boolean) => void

  todoProgressExpanded: boolean
  setTodoProgressExpanded: (expanded: boolean) => void

  focusFilesExpanded: boolean
  setFocusFilesExpanded: (collapsed: boolean) => void

  // MARK: Panel tabs
  panelTabs: PanelTab[]
  activeTabId: string | null
  addPanelTab: (
    entrypointId: EntrypointId,
    options?: { reuseExisting?: boolean },
  ) => string
  removePanelTab: (tabId: string, nextActiveTabId?: string | null) => void
  reorderPanelTabs: (activeTabId: string, overTabId: string) => void
  replacePanelTabEntrypoint: (
    tabId: string,
    entrypointId: EntrypointId,
    options?: { preserveNav?: boolean },
  ) => void
  setActiveTabId: (tabId: string | null) => void
  updateTabNavById: (tabId: string, update: Partial<TabNavState>) => void
  resetPanelTabs: () => void

  /**
   * True when the file panel is animating (expanding/collapsing).
   * Used to pause virtualization measurements during animation to prevent jittering.
   */
  isPanelAnimating: boolean
  setIsPanelAnimating: (isAnimating: boolean) => void

  /**
   * True when the sidebar is animating (opening/closing).
   * Used to pause virtualization measurements during animation to prevent jittering.
   */
  isSidebarAnimating: boolean
  setIsSidebarAnimating: (isAnimating: boolean) => void

  makeSendMessageParams: (
    opts?: MakeSendMessageParamsOptions,
  ) => WithOverrideModelName &
    WithWebSearchAnswerTool &
    WithFocusFiles &
    WithViewportContext &
    WithSelectedMcpServers &
    WithSelectedConnectorSources
}

const getOpportunityFileIds = (attachments: Attachment[]) => {
  return attachments
    .filter((a) => {
      return (
        a.type === 'federal_contract_file' ||
        a.type === 'federal_grant_file' ||
        a.type === 'sbir_file' ||
        a.type === 'sled_contract_file'
      )
    })
    .map((a) => a.id)
}

const getOrganizationFileIds = (attachments: Attachment[]) => {
  return attachments
    .filter((a) => {
      return a.type === 'organization_file'
    })
    .map((a) => a.id)
}

const getPursuitFileIds = (attachments: Attachment[]) => {
  return attachments
    .filter((a) => {
      return a.type === 'pursuit_file'
    })
    .map((a) => a.id)
}

const getUserFileIds = (attachments: Attachment[]) => {
  return attachments
    .filter((a) => {
      return a.type === 'user_file'
    })
    .map((a) => a.id)
}

interface InitChatEnvironmentStoreStateArgs {
  modelOptions: ModelOption[]
  defaultModel: ModelOption
  sourceViewerState: SourceViewerState
  defaultSidebarOpen?: boolean
  persistedPanelState?: PersistedPanelState | null
}

export const resolveModelOption = (storedValue: string): ModelOption => {
  const canonicalOption = MODEL_SELECTOR_OPTIONS.find(
    (option) => option.value === storedValue,
  )
  if (canonicalOption) {
    return canonicalOption
  }

  // Older picks named a vendor rather than a model; each lands on that vendor's model.
  if (storedValue === 'sweetspot:opus') return VENDOR_MODEL_SELECTORS.anthropic
  const vendor = vendorOfOption(storedValue)
  return vendor ? VENDOR_MODEL_SELECTORS[vendor] : AUTO_MODEL_SELECTOR
}

const DEFAULT_MODEL_OPTIONS = MODEL_SELECTOR_OPTIONS

export interface PersistedPanelState {
  panelTabs: PanelTab[]
  activeTabId: string | null
  sourceViewerState: SourceViewerState
  sidebarOpen: boolean
}

const DEFAULT_SIDEBAR_OPEN = true
export const DEFAULT_NEW_CHAT_SIDEBAR_OPEN = true
// v4: sidebar defaults back to open (v3 briefly flipped it closed); older
// versions reset sidebarOpen so the restored default applies once, after
// which user toggles persist and win.
const CHAT_PANEL_STATE_STORAGE_VERSION = 4

const isSourceViewerState = (value: unknown): value is SourceViewerState => {
  return value === 'hidden' || value === 'open'
}

const normalizePersistedSourceViewerState = (
  value: unknown,
): SourceViewerState => {
  if (isSourceViewerState(value)) return value
  // `collapsed` was the pre-split-panel persisted value for a hidden viewer.
  if (value === 'collapsed') return 'hidden'
  return 'open'
}

// The pursuit and opportunity files entrypoints are agent-scoped: each is
// allowed only in its own agent's tab set. Persisted state can still hold the
// other agent's tab — sessions saved before the opportunity_files cutover
// have pursuit_files tabs, and the new-chat storage key is shared across
// agents. Restored as-is such tabs render as hidden, uncloseable tabs that
// still serialize into agent context and intercept openFileInPanel's file-id
// reuse; remap them to this agent's files entrypoint instead.
const AGENT_SCOPED_FILES_ENTRYPOINTS: Partial<Record<EntrypointId, true>> = {
  pursuit_files: true,
  opportunity_files: true,
}

export const migratePersistedPanelState = (
  state: PersistedPanelState | null,
  agentName: AgentName,
): PersistedPanelState | null => {
  if (!state) return null
  const filesEntrypointId = getFilesEntrypointForAgent(agentName)
  let changed = false
  const panelTabs = state.panelTabs.map((tab) => {
    if (
      !AGENT_SCOPED_FILES_ENTRYPOINTS[tab.entrypointId] ||
      tab.entrypointId === filesEntrypointId
    ) {
      return tab
    }
    changed = true
    return { ...tab, entrypointId: filesEntrypointId }
  })
  return changed ? { ...state, panelTabs } : state
}

const normalizePersistedPanelState = (
  value: unknown,
): PersistedPanelState | null => {
  if (!value || typeof value !== 'object') return null
  const candidate = value as Partial<PersistedPanelState>
  if (!Array.isArray(candidate.panelTabs)) return null

  const panelTabs = candidate.panelTabs
    .filter(
      (tab): tab is PanelTab =>
        Boolean(tab) &&
        typeof tab.id === 'string' &&
        isEntrypointId(tab.entrypointId) &&
        (('nav' in tab && typeof tab.nav === 'object' && tab.nav !== null) ||
          ('navStack' in tab &&
            Array.isArray((tab as { navStack?: unknown }).navStack))),
    )
    .map((tab) => {
      const legacyNavStack =
        (tab as { navStack?: TabNavState[] }).navStack ?? []
      return {
        ...tab,
        nav:
          'nav' in tab && tab.nav && typeof tab.nav === 'object'
            ? tab.nav
            : (legacyNavStack[legacyNavStack.length - 1] ??
              createListNavState()),
      }
    })

  const activeTabId =
    typeof candidate.activeTabId === 'string' ? candidate.activeTabId : null
  const safeActiveTabId = panelTabs.some((tab) => tab.id === activeTabId)
    ? activeTabId
    : (panelTabs[panelTabs.length - 1]?.id ?? null)

  return {
    panelTabs,
    activeTabId: safeActiveTabId,
    sourceViewerState: normalizePersistedSourceViewerState(
      candidate.sourceViewerState,
    ),
    sidebarOpen:
      typeof candidate.sidebarOpen === 'boolean'
        ? candidate.sidebarOpen
        : DEFAULT_SIDEBAR_OPEN,
  }
}

export const loadPersistedPanelState = (
  storageKey: string | undefined,
): PersistedPanelState | null => {
  if (!storageKey || typeof window === 'undefined') return null
  try {
    const serialized = window.localStorage.getItem(storageKey)
    if (!serialized) return null
    const parsed = JSON.parse(serialized) as {
      version?: number
      state?: unknown
    }
    const normalized = normalizePersistedPanelState(parsed.state)
    if (!normalized) return null

    if (parsed.version === CHAT_PANEL_STATE_STORAGE_VERSION) {
      return normalized
    }

    // v1-v3 predate the restored open-by-default sidebar (v3 briefly forced
    // it closed): apply the open default once, keeping tabs/viewer state.
    if (parsed.version === 1 || parsed.version === 2 || parsed.version === 3) {
      return {
        ...normalized,
        sidebarOpen: DEFAULT_SIDEBAR_OPEN,
      }
    }

    return null
  } catch {
    return null
  }
}

const sanitizeTabNavForPersistence = (nav: TabNavState): TabNavState => {
  const {
    activeSourceSnapshot,
    draftToolCallId,
    sourceActivationKey,
    ...rest
  } = nav
  return rest
}

const sanitizePanelTabsForPersistence = (panelTabs: PanelTab[]): PanelTab[] => {
  return panelTabs.map((tab) => ({
    ...tab,
    nav: sanitizeTabNavForPersistence(tab.nav ?? createListNavState()),
  }))
}

export const selectPersistedPanelState = (
  state: ChatEnvironmentStoreState,
): PersistedPanelState => {
  return {
    panelTabs: state.panelTabs,
    activeTabId: state.activeTabId,
    sourceViewerState: state.sourceViewerState,
    sidebarOpen: state.sidebarOpen,
  }
}

const equalPersistedPanelState = (
  left: PersistedPanelState,
  right: PersistedPanelState,
) => {
  return (
    left.panelTabs === right.panelTabs &&
    left.activeTabId === right.activeTabId &&
    left.sourceViewerState === right.sourceViewerState &&
    left.sidebarOpen === right.sidebarOpen
  )
}

export const persistPanelState = (
  storageKey: string | undefined,
  panelState: PersistedPanelState,
) => {
  if (!storageKey || typeof window === 'undefined') return

  const sanitizedPanelState: PersistedPanelState = {
    ...panelState,
    panelTabs: sanitizePanelTabsForPersistence(panelState.panelTabs),
  }

  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        version: CHAT_PANEL_STATE_STORAGE_VERSION,
        state: sanitizedPanelState,
      }),
    )
  } catch {
    // noop
  }
}

export const getPanelStateStorageKey = ({
  agentNameForUi,
  orgId,
  pursuitId,
  opportunityId,
  sessionId,
}: {
  agentNameForUi: AgentName
  orgId: UUID
  pursuitId?: UUID
  opportunityId?: UUID
  sessionId?: UUID
}) => {
  // New-chat chrome (sidebar open and workspace open/closed) is one GLOBAL
  // default across every agent, org, and pursuit new-chat screen: set it once
  // anywhere and it applies everywhere, while per-session state stays scoped.
  // New-chat keys never retain tab content because tabs are stripped on entry
  // and on send, so sharing this key cannot leak org- or pursuit-specific tabs.
  if (sessionId === undefined) {
    return ['chat-v2', 'panel-state-v3', 'new'].join(':')
  }

  const storageScopeId = pursuitId ?? opportunityId ?? 'none'

  return [
    'chat-v2',
    'panel-state-v3',
    agentNameForUi,
    orgId,
    storageScopeId,
    sessionId,
  ].join(':')
}

export const setPersistedPanelChromeState = ({
  agentNameForUi,
  orgId,
  pursuitId,
  sessionId,
  sourceViewerState,
  sidebarOpen,
}: {
  agentNameForUi: AgentName
  orgId: UUID
  pursuitId?: UUID
  sessionId?: UUID
  sourceViewerState: SourceViewerState
  sidebarOpen: boolean
}) => {
  if (typeof window === 'undefined') return

  const storageKey = getPanelStateStorageKey({
    agentNameForUi,
    orgId,
    pursuitId,
    sessionId,
  })

  try {
    const nextState: PersistedPanelState = {
      panelTabs: [],
      activeTabId: null,
      sourceViewerState,
      sidebarOpen,
    }

    persistPanelState(storageKey, nextState)
  } catch {
    // Best effort only: navigation should never fail because storage failed.
  }
}

// The picker keeps the listed order; a default missing from the list joins at the end.
const withDefaultModelOption = (args: InitChatEnvironmentStoreStateArgs) => {
  const inputOptions: ModelOption[] = []
  for (const option of args.modelOptions) {
    const resolvedOption = resolveModelOption(option.value)
    if (!inputOptions.some(({ value }) => value === resolvedOption.value)) {
      inputOptions.push(cloneDeep(resolvedOption))
    }
  }

  const workingOptions =
    inputOptions.length > 0 ? inputOptions : cloneDeep(DEFAULT_MODEL_OPTIONS)
  let defaultOption = cloneDeep(resolveModelOption(args.defaultModel.value))

  const existingIndex = findIndex(
    workingOptions,
    (option) => option.value === defaultOption.value,
  )

  if (existingIndex !== -1) {
    defaultOption = workingOptions[existingIndex]
  } else {
    workingOptions.push(defaultOption)
  }

  return {
    modelOptions: workingOptions,
    selectedModel: defaultOption,
  }
}

const getTopTabNav = (tab: PanelTab | null | undefined): TabNavState | null => {
  if (!tab) return null
  return tab.nav ?? null
}

const getActiveSourceIdFromTab = (
  tab: PanelTab | null | undefined,
): number | string | null => {
  const nav = getTopTabNav(tab)
  if (!nav || nav.activeSourceId === undefined) return null
  return nav.activeSourceId
}

const getPanelActiveState = (
  panelTabs: PanelTab[],
  activeTabId: string | null,
) => {
  const activeTab = activeTabId
    ? panelTabs.find((tab) => tab.id === activeTabId)
    : null
  const safeActiveTabId = activeTab?.id ?? null
  return {
    activeTabId: safeActiveTabId,
    activeSourceId: getActiveSourceIdFromTab(activeTab),
  }
}

/**
 * A chip stays lit only while some tab still carries the reveal it armed
 * (its nav's `viewerInitialLocatorKey` — the key the chip was latched
 * under). Tab teardown never reaches an in-viewer dismissal — closing the
 * tab, resetting the panel, or a different file replacing the detail view
 * unmounts the viewer outright — so staleness is settled at the store,
 * where every one of those transitions lands.
 */
const getStaleSelectionChipState = (
  panelTabs: PanelTab[],
  chip: { key: number; signature: string } | null,
): { activeSelectionChip: null } | Record<string, never> =>
  chip === null ||
  panelTabs.some((tab) => tab.nav?.viewerInitialLocatorKey === chip.key)
    ? {}
    : { activeSelectionChip: null }

const clearPersistedPanelContent = (
  panelState: PersistedPanelState | null,
): PersistedPanelState | null => {
  if (!panelState) return null
  // Fresh chats drop TAB CONTENT only. Chrome preferences (sidebar and
  // workspace open/closed) are the user's and must survive new-chat visits.
  return {
    ...panelState,
    panelTabs: [],
    activeTabId: null,
  }
}

const hasNavUpdateChanged = (
  current: TabNavState,
  update: Partial<TabNavState>,
) => {
  return Object.entries(update).some(([key, value]) => {
    return current[key as keyof TabNavState] !== value
  })
}

const initChatEnvironmentStoreState: (
  args: InitChatEnvironmentStoreStateArgs,
) => StateCreator<ChatEnvironmentStoreState> = ({
  defaultModel,
  defaultSidebarOpen = DEFAULT_SIDEBAR_OPEN,
  modelOptions,
  sourceViewerState,
  persistedPanelState = null,
}) => {
  // Workspace can be empty: when no tabs exist, the new-tab menu renders in
  // the workspace area. Persisted tabs (if any) are restored as-is.
  const initialPanelTabs = persistedPanelState?.panelTabs ?? []
  const initialActiveTabId =
    persistedPanelState?.activeTabId &&
    initialPanelTabs.some((tab) => tab.id === persistedPanelState.activeTabId)
      ? persistedPanelState.activeTabId
      : (initialPanelTabs[initialPanelTabs.length - 1]?.id ?? null)
  const initialPanelActiveState = getPanelActiveState(
    initialPanelTabs,
    initialActiveTabId,
  )
  const initialSourceViewerState: SourceViewerState =
    persistedPanelState?.sourceViewerState ?? sourceViewerState
  const initialSidebarOpen =
    persistedPanelState?.sidebarOpen ?? defaultSidebarOpen

  return (set, get) => ({
    isLoading: false,
    setIsLoading: (isLoading) => set({ isLoading }),

    sidebarOpen: initialSidebarOpen,
    setSidebarOpen: (open) => set({ sidebarOpen: open }),

    ...withDefaultModelOption({
      defaultModel,
      modelOptions,
      sourceViewerState,
    }),
    setSelectedModel: (modelName: KnownModelName | string) => {
      const state = get()
      const resolvedOption = resolveModelOption(modelName)
      const option =
        state.modelOptions.find(
          ({ value }) => value === resolvedOption.value,
        ) ?? resolvedOption
      if (state.selectedModel.value === option.value) {
        return
      }
      set({ selectedModel: option })
    },

    selectedTools: [],
    setSelectedTools: (tools) => set({ selectedTools: tools }),
    addTool: (tool) =>
      set((state) => ({
        selectedTools: state.selectedTools.includes(tool)
          ? state.selectedTools
          : [tool], // Only allow one tool at a time
      })),
    removeTool: (tool) =>
      set((state) => ({
        selectedTools: state.selectedTools.filter((t) => t !== tool),
      })),

    // MCP server integrations - default to empty (user must explicitly enable)
    selectedIntegrations: [],
    setSelectedIntegrations: (integrations) =>
      set({ selectedIntegrations: integrations }),
    addIntegration: (integrationId) =>
      set((state) => ({
        selectedIntegrations: state.selectedIntegrations.includes(integrationId)
          ? state.selectedIntegrations
          : [...state.selectedIntegrations, integrationId],
      })),
    removeIntegration: (integrationId) =>
      set((state) => ({
        selectedIntegrations: state.selectedIntegrations.filter(
          (id) => id !== integrationId,
        ),
      })),

    // Connector data sources - default to empty (user must explicitly enable)
    selectedConnectorSources: [],
    setSelectedConnectorSources: (sources) =>
      set({ selectedConnectorSources: sources }),
    addConnectorSource: (source) =>
      set((state) => ({
        selectedConnectorSources: state.selectedConnectorSources.includes(
          source,
        )
          ? state.selectedConnectorSources
          : [...state.selectedConnectorSources, source],
      })),
    removeConnectorSource: (source) =>
      set((state) => ({
        selectedConnectorSources: state.selectedConnectorSources.filter(
          (s) => s !== source,
        ),
      })),

    searchQuery: '',
    setSearchQuery: (query) => set({ searchQuery: query }),

    expandedMarkdownTableKey: null,
    setExpandedMarkdownTableKey: (key) =>
      typeof key === 'function'
        ? set({ expandedMarkdownTableKey: key(get().expandedMarkdownTableKey) })
        : set({ expandedMarkdownTableKey: key }),

    toolCardExpansion: {},
    setToolCardExpanded: ({ key, expanded }) =>
      set((state) => ({
        toolCardExpansion: { ...state.toolCardExpansion, [key]: expanded },
      })),

    documentVersionSurfaces: {},
    registerDocumentVersionSurface: ({ draftId, surfaceId }) => {
      set((state) => ({
        documentVersionSurfaces: {
          ...state.documentVersionSurfaces,
          [draftId]: [
            ...(state.documentVersionSurfaces[draftId] ?? []),
            surfaceId,
          ],
        },
      }))
      return () =>
        set((state) => {
          const surfaces = { ...state.documentVersionSurfaces }
          const remaining =
            surfaces[draftId]?.filter((id) => id !== surfaceId) ?? []
          if (remaining.length) surfaces[draftId] = remaining
          else delete surfaces[draftId]
          return { documentVersionSurfaces: surfaces }
        })
    },

    documentVersionReviews: {},
    updateDocumentVersionReview: ({ draftId, update }) =>
      set((state) => ({
        documentVersionReviews: {
          ...state.documentVersionReviews,
          [draftId]: {
            changed: null,
            confirmationOwner: null,
            regenerated: false,
            ...state.documentVersionReviews[draftId],
            ...update,
          },
        },
      })),

    selectedSessionIds: [],
    setSelectedSessionIds: (ids) => set({ selectedSessionIds: ids }),
    setSessionSelected: (id, selected) =>
      set((state) => ({
        selectedSessionIds: selected
          ? [...state.selectedSessionIds, id]
          : state.selectedSessionIds.filter((sessionId) => sessionId !== id),
      })),
    clearSessionSelection: () => set({ selectedSessionIds: [] }),

    submitPending: false,
    setSubmitPending: (submitPending) => set({ submitPending }),

    submitDisabled: false,
    setSubmitDisabled: (submitDisabled) => set({ submitDisabled }),
    chatContinuationRequestMessageId: null,
    setChatContinuationRequestMessageId: (messageId) =>
      typeof messageId === 'function'
        ? set({
            chatContinuationRequestMessageId: messageId(
              get().chatContinuationRequestMessageId,
            ),
          })
        : set({ chatContinuationRequestMessageId: messageId }),

    activeSourceId: initialPanelActiveState.activeSourceId,
    setActiveSourceId: (sourceId) =>
      typeof sourceId === 'function'
        ? set({ activeSourceId: sourceId(get().activeSourceId) })
        : set({ activeSourceId: sourceId }),

    attachments: [],
    addAttachment: (attachment) =>
      set({ attachments: [...get().attachments, attachment] }),
    removeAttachment: (attachmentId) =>
      set({
        attachments: get().attachments.filter((a) => a.id !== attachmentId),
      }),
    clearAttachments: () => set({ attachments: [] }),
    setAttachments: (attachments) => set({ attachments }),
    convertPendingToComplete: (attachmentId: UUID, file: ServerFile) => {
      set((state) => ({
        attachments: state.attachments.map((a) =>
          a.id === attachmentId
            ? // we MUST change the file id because the original id
              // is created client side and thus does not match the server id
              { ...a, id: file.id, status: 'complete', file: file }
            : a,
        ),
      }))
    },

    // Image attachments (sent as ImageUrl content in messages)
    imageAttachments: [],
    getImageAttachments: () => get().imageAttachments,
    addImageAttachment: (attachment) =>
      set({ imageAttachments: [...get().imageAttachments, attachment] }),
    updateImageAttachment: (attachmentId, update) => {
      set((state) => ({
        imageAttachments: state.imageAttachments.map((a) =>
          a.id === attachmentId ? ({ ...a, ...update } as ImageAttachment) : a,
        ),
      }))
    },
    removeImageAttachment: (attachmentId) =>
      set({
        imageAttachments: get().imageAttachments.filter(
          (a) => a.id !== attachmentId,
        ),
      }),
    clearImageAttachments: () => set({ imageAttachments: [] }),
    setImageAttachments: (attachments) =>
      set({ imageAttachments: attachments }),

    pastedTextCards: [],
    getPastedTextCards: () => get().pastedTextCards,
    addPastedTextCard: (card) =>
      set({ pastedTextCards: [...get().pastedTextCards, card] }),
    addPlaysCards: (cards) => {
      const { pastedTextCards } = get()
      const staged = new Set(pastedTextCards.map(playsCardKey))
      const fresh = cards.filter((card) => !staged.has(playsCardKey(card)))
      if (fresh.length) set({ pastedTextCards: [...pastedTextCards, ...fresh] })
    },
    updatePastedTextCard: (cardId, text) =>
      set({
        pastedTextCards: get().pastedTextCards.map((c) =>
          c.id === cardId
            ? {
                ...c,
                text,
                title:
                  c.source === 'paste'
                    ? createPastedTextCardTitle(text)
                    : c.title,
              }
            : c,
        ),
      }),
    removePastedTextCard: (cardId) =>
      set({
        pastedTextCards: get().pastedTextCards.filter((c) => c.id !== cardId),
      }),
    clearPastedTextCards: () => set({ pastedTextCards: [] }),
    setPastedTextCards: (cards) => set({ pastedTextCards: cards }),

    isRecording: false,
    setIsRecording: (isRecording) => set({ isRecording }),

    todoProgressExpanded: false,
    setTodoProgressExpanded: (expanded) =>
      set({ todoProgressExpanded: expanded }),

    focusFilesExpanded: false,
    setFocusFilesExpanded: (collapsed) =>
      set({ focusFilesExpanded: collapsed }),

    // MARK: Panel tabs
    panelTabs: initialPanelTabs,
    activeTabId: initialPanelActiveState.activeTabId,
    addPanelTab: (entrypointId, options) => {
      const reuseExisting = options?.reuseExisting ?? true
      if (reuseExisting) {
        // Reuse the most recent tab for this entrypoint if one exists.
        const matchingTabs = get().panelTabs.filter(
          (tab) => tab.entrypointId === entrypointId,
        )
        const existing = matchingTabs[matchingTabs.length - 1]
        if (existing) {
          set((state) => getPanelActiveState(state.panelTabs, existing.id))
          return existing.id
        }
      }
      const tab = createPanelTab(entrypointId)
      set((state) => {
        const panelTabs = [...state.panelTabs, tab]
        return {
          panelTabs,
          ...getPanelActiveState(panelTabs, tab.id),
        }
      })
      return tab.id
    },
    removePanelTab: (tabId, nextActiveTabId) => {
      set((state) => {
        const removedIndex = state.panelTabs.findIndex(
          (tab) => tab.id === tabId,
        )
        if (removedIndex === -1) {
          return {}
        }

        const panelTabs = state.panelTabs.filter((t) => t.id !== tabId)
        // Callers with a filtered tab view can provide the visible neighbor.
        // Otherwise use the full-list right neighbor, falling back left.
        const fallbackActiveTabId =
          (panelTabs[removedIndex] ?? panelTabs[removedIndex - 1])?.id ?? null
        const activeTabId =
          state.activeTabId === tabId
            ? nextActiveTabId === undefined
              ? fallbackActiveTabId
              : nextActiveTabId
            : state.activeTabId
        return {
          panelTabs,
          ...getPanelActiveState(panelTabs, activeTabId),
          ...getStaleSelectionChipState(panelTabs, state.activeSelectionChip),
        }
      })
    },
    reorderPanelTabs: (activeTabId, overTabId) => {
      set((state) => {
        if (activeTabId === overTabId) return {}
        const activeIndex = state.panelTabs.findIndex(
          (tab) => tab.id === activeTabId,
        )
        const overIndex = state.panelTabs.findIndex(
          (tab) => tab.id === overTabId,
        )
        if (activeIndex === -1 || overIndex === -1) return {}

        const panelTabs = [...state.panelTabs]
        const [activeTab] = panelTabs.splice(activeIndex, 1)
        panelTabs.splice(overIndex, 0, activeTab)
        return { panelTabs }
      })
    },
    replacePanelTabEntrypoint: (tabId, entrypointId, options) => {
      set((state) => {
        if (!state.panelTabs.some((tab) => tab.id === tabId)) {
          return {}
        }

        const panelTabs = state.panelTabs.map((tab) => {
          if (tab.id !== tabId) {
            return tab
          }
          return {
            ...tab,
            entrypointId,
            nav: options?.preserveNav ? tab.nav : createListNavState(),
          }
        })
        return {
          panelTabs,
          ...getPanelActiveState(panelTabs, state.activeTabId),
          ...getStaleSelectionChipState(panelTabs, state.activeSelectionChip),
        }
      })
    },
    setActiveTabId: (tabId) =>
      set((state) => {
        return getPanelActiveState(state.panelTabs, tabId)
      }),
    updateTabNavById: (tabId, update) => {
      set((state) => {
        let changed = false
        const panelTabs = state.panelTabs.map((t) => {
          if (t.id !== tabId) return t
          const current = t.nav ?? createListNavState()
          if (!hasNavUpdateChanged(current, update)) return t
          changed = true
          return { ...t, nav: { ...current, ...update } }
        })
        if (!changed) return {}
        return {
          panelTabs,
          ...getPanelActiveState(panelTabs, state.activeTabId),
          ...getStaleSelectionChipState(panelTabs, state.activeSelectionChip),
        }
      })
    },
    resetPanelTabs: () =>
      set({
        panelTabs: [],
        activeTabId: null,
        activeSourceId: null,
        activeSelectionChip: null,
      }),

    isPanelAnimating: false,
    setIsPanelAnimating: (isAnimating) =>
      set({ isPanelAnimating: isAnimating }),

    isSidebarAnimating: false,
    setIsSidebarAnimating: (isAnimating) =>
      set({ isSidebarAnimating: isAnimating }),

    sourceViewerState: initialSourceViewerState,
    viewerStateByTabId: {},
    setViewerStateByTabId: (tabId, state) =>
      set((current) => {
        if (state === null) {
          if (!(tabId in current.viewerStateByTabId)) return {}
          const viewerStateByTabId = { ...current.viewerStateByTabId }
          delete viewerStateByTabId[tabId]
          return { viewerStateByTabId }
        }
        return {
          viewerStateByTabId: {
            ...current.viewerStateByTabId,
            [tabId]: state,
          },
        }
      }),
    viewerSelectionResetNonce: 0,
    bumpViewerSelectionResetNonce: () =>
      set((current) => ({
        viewerSelectionResetNonce: current.viewerSelectionResetNonce + 1,
      })),
    staleSelectionTabIds: [],
    markSelectionsStale: (tabIds) =>
      set((current) => ({
        staleSelectionTabIds: [
          ...current.staleSelectionTabIds.filter((id) => !tabIds.includes(id)),
          ...tabIds,
        ],
      })),
    takeStaleSelection: (tabId) => {
      if (!get().staleSelectionTabIds.includes(tabId)) return false
      set((current) => ({
        staleSelectionTabIds: current.staleSelectionTabIds.filter(
          (id) => id !== tabId,
        ),
      }))
      return true
    },
    composerFocusNonce: 0,
    requestComposerFocus: () =>
      set((current) => ({
        composerFocusNonce: current.composerFocusNonce + 1,
      })),
    // Hiding the panel drops the full-screen expansion: the expanded pane
    // covers the whole chat surface and marks the chat inert, so a stale flag
    // would reopen the workspace straight into full screen with the chat
    // unreachable underneath.
    setSourceViewerState: (sourceViewerState) => {
      const next =
        typeof sourceViewerState === 'function'
          ? sourceViewerState(get().sourceViewerState)
          : sourceViewerState
      set({
        sourceViewerState: next,
        ...(next === 'hidden' ? { panelExpanded: false } : null),
      })
    },
    activeSelectionChip: null,
    setActiveSelectionChip: (chip) => set({ activeSelectionChip: chip }),
    dismissActiveSelectionChip: (key) => {
      if (get().activeSelectionChip?.key === key) {
        set({ activeSelectionChip: null })
      }
    },
    sessionHistoryOpen: false,
    setSessionHistoryOpen: (open) => set({ sessionHistoryOpen: open }),

    fullScreenViewerOpen: false,
    setFullScreenViewerOpen: (open) => set({ fullScreenViewerOpen: open }),
    fullScreenViewerFileId: null,
    setFullScreenViewerFileId: (fileId) =>
      set({ fullScreenViewerFileId: fileId }),
    openFullScreenViewer: (fileId) =>
      set({ fullScreenViewerOpen: true, fullScreenViewerFileId: fileId }),
    closeFullScreenViewer: () =>
      set({ fullScreenViewerOpen: false, fullScreenViewerFileId: null }),

    panelExpanded: false,
    setPanelExpanded: (expanded) => set({ panelExpanded: expanded }),

    makeSendMessageParams: (opts) => {
      const state = get()
      const { attachments } = state

      const opportunityFileIds = getOpportunityFileIds(attachments)
      const organizationFileIds = getOrganizationFileIds(attachments)
      const pursuitFileIds = getPursuitFileIds(attachments)
      const userFileIds = getUserFileIds(attachments)

      const activeTab = state.activeTabId
        ? state.panelTabs.find((tab) => tab.id === state.activeTabId)
        : null
      // Location only: a live highlight is not sent implicitly — selections
      // travel solely via the explicit "Add to Chat" selection card inline in
      // the user message.
      const viewportContext = stripViewportContextSelection(
        getViewportContextFromPanel({
          tab: activeTab ?? undefined,
          tabs: state.panelTabs,
          sourceViewerOpen: state.sourceViewerState === 'open',
          viewerState: activeTab
            ? (readLatestViewerState(activeTab.id) ??
              state.viewerStateByTabId[activeTab.id])
            : undefined,
        }),
      )
      const searchMethod: WebSearchMethod = IS_FEDRAMP_ENVIRONMENT
        ? 'disabled'
        : includes(state.selectedTools, 'deep_research')
          ? 'deep-research'
          : includes(state.selectedTools, 'web_search')
            ? 'force-web-search'
            : 'allow-web-search'

      const params = {
        override_model_name: state.selectedModel.value,
        search_method: searchMethod,
        focus_opportunity_file_ids: opportunityFileIds,
        focus_organization_file_ids: organizationFileIds,
        focus_pursuit_file_ids: pursuitFileIds,
        focus_user_file_ids: userFileIds,
        viewport_context: viewportContext,
        selected_mcp_server_ids: state.selectedIntegrations,
        selected_connector_sources: state.selectedConnectorSources,
      }

      // Message-scoped actions never attach the live composer draft. The
      // shared helper owns the canonical strip list for exports and hidden
      // continuation requests.
      return opts?.excludeComposerDraft
        ? withoutComposerDraftParams(params)
        : params
    },
  })
}

export type ChatEnvironmentStore = ReturnType<typeof createChatEnvironmentStore>

export const createChatEnvironmentStore = (
  args: InitChatEnvironmentStoreStateArgs,
) => {
  return createStore<
    ChatEnvironmentStoreState,
    [
      ['zustand/devtools', never],
      ['zustand/subscribeWithSelector', never],
      ['zustand/immer', never],
    ]
  >(
    devtools(
      subscribeWithSelector(
        immer<ChatEnvironmentStoreState>(initChatEnvironmentStoreState(args)),
      ),
      {
        name: 'Chat Store',
      },
    ),
  )
}

export const ChatEnvironmentStoreContext = createContext<
  ChatEnvironmentStore | undefined
>(undefined)

interface ChatEnvironmentStoreProviderProps extends Omit<
  InitChatEnvironmentStoreStateArgs,
  'persistedPanelState'
> {
  agentNameForUi: AgentName
  panelStateStorageKey?: string
  resetPersistedPanelContent?: boolean
  children: React.ReactNode
}

export const ChatEnvironmentStoreProvider: React.FC<
  ChatEnvironmentStoreProviderProps
> = ({
  children,
  agentNameForUi,
  panelStateStorageKey,
  resetPersistedPanelContent = false,
  ...storeArgs
}) => {
  const [persistedPanelState] = useState<PersistedPanelState | null>(() => {
    const loadedPanelState = migratePersistedPanelState(
      loadPersistedPanelState(panelStateStorageKey),
      agentNameForUi,
    )
    return resetPersistedPanelContent
      ? clearPersistedPanelContent(loadedPanelState)
      : loadedPanelState
  })
  const storeRef = useRef<ChatEnvironmentStore>(
    createChatEnvironmentStore({
      ...storeArgs,
      persistedPanelState,
    }),
  )

  useEffect(() => {
    if (!panelStateStorageKey) return
    const store = storeRef.current
    persistPanelState(
      panelStateStorageKey,
      selectPersistedPanelState(store.getState()),
    )

    return store.subscribe(
      selectPersistedPanelState,
      (nextPersistedPanelState) => {
        persistPanelState(panelStateStorageKey, nextPersistedPanelState)
      },
      { equalityFn: equalPersistedPanelState },
    )
  }, [panelStateStorageKey])

  return (
    <ChatEnvironmentStoreContext.Provider value={storeRef.current}>
      {children}
    </ChatEnvironmentStoreContext.Provider>
  )
}

export const useChatEnvironmentStore = () => {
  const store = useContext(ChatEnvironmentStoreContext)
  if (!store) {
    throw new Error(
      'useChatEnvironmentStore must be used within an ChatEnvironmentStoreProvider',
    )
  }
  return store
}

// MARK: base chat environment provider

interface ChatConfigurationContextProps {
  agentNameForUi: AgentName
  orgId: UUID
  newChatLink: string
  recentChatsLink: string
  promptsLink: string
  queryKeys: ChatSessionListQueryKeyArgs[]
  getSessionLink: (session: ChatSessionListItem) => string
  sessionId?: UUID
  pursuitId?: UUID
}

interface ChatConfigurationProviderProps {
  agentNameForUi: AgentName
  orgId: UUID
  newChatLink: string
  recentChatsLink: string
  promptsLink: string
  queryKeys: ChatSessionListQueryKeyArgs[]
  getSessionLink: (session: ChatSessionListItem) => string
  sessionId?: UUID
  pursuitId?: UUID
  defaultModel: ModelOption
  modelOptions: ModelOption[]
  defaultSidebarOpen?: boolean
  resetPersistedPanelContent?: boolean
  sourceViewerState?: SourceViewerState
  children: React.ReactNode
}

export const ChatConfigurationContext = createContext<
  ChatConfigurationContextProps | undefined
>(undefined)

export const ChatConfigurationProvider: React.FC<
  ChatConfigurationProviderProps
> = ({
  agentNameForUi,
  orgId,
  newChatLink,
  recentChatsLink,
  promptsLink,
  queryKeys,
  sessionId,
  defaultModel,
  defaultSidebarOpen = DEFAULT_SIDEBAR_OPEN,
  resetPersistedPanelContent = false,
  modelOptions,
  sourceViewerState = 'open',
  getSessionLink,
  pursuitId,
  children,
}) => {
  const panelStateStorageKey = getPanelStateStorageKey({
    agentNameForUi,
    orgId,
    pursuitId,
    sessionId,
  })

  // The store's REMOUNT key must track the chat identity, not the storage
  // key: new chats share one global storage key for chrome persistence, but
  // the Pages Router reuses the page component across dynamic-param
  // navigations (org A new chat -> org B new chat), so a constant React key
  // would carry the previous context's in-memory tabs/attachments into the
  // next one. Remounting per identity re-runs the mount-time content strip.
  const storeIdentityKey = [
    agentNameForUi,
    orgId,
    pursuitId ?? 'none',
    sessionId ?? 'new',
  ].join(':')

  // Every sidebar row reads this context (useChatOrgId, useChatSessionId,
  // useChatGetSessionLink), and a context consumer re-renders on value
  // identity, not on the field it reads - so an inline object here re-renders
  // the whole chat tree on any parent render and silently defeats the memo()
  // on SessionItem. Callers keep the reference deps stable: getSessionLink is
  // useStableCallback in every adapter, queryKeys is memoized at its hook.
  const configuration = useMemo(
    () => ({
      agentNameForUi,
      orgId,
      newChatLink,
      recentChatsLink,
      promptsLink,
      queryKeys,
      getSessionLink,
      sessionId,
      pursuitId,
    }),
    [
      agentNameForUi,
      orgId,
      newChatLink,
      recentChatsLink,
      promptsLink,
      queryKeys,
      getSessionLink,
      sessionId,
      pursuitId,
    ],
  )

  return (
    <ChatConfigurationContext.Provider value={configuration}>
      <ChatEnvironmentStoreProvider
        key={storeIdentityKey}
        agentNameForUi={agentNameForUi}
        defaultModel={defaultModel}
        defaultSidebarOpen={defaultSidebarOpen}
        modelOptions={modelOptions}
        resetPersistedPanelContent={resetPersistedPanelContent}
        sourceViewerState={sourceViewerState}
        panelStateStorageKey={panelStateStorageKey}
      >
        {children}
      </ChatEnvironmentStoreProvider>
    </ChatConfigurationContext.Provider>
  )
}

// MARK: chat environment provider

interface ChatEnvironmentContextProps {
  agentName: AgentName
  compact: boolean
  sourceViewerState: SourceViewerState
}

interface ChatEnvironmentProviderProps {
  agentName: AgentName
  orgId: UUID
  newChatLink: string
  recentChatsLink: string
  promptsLink: string
  getSessionLink: (session: ChatSessionListItem) => string
  queryKeys: ChatSessionListQueryKeyArgs[]
  sessionId?: UUID
  pursuitId?: UUID
  defaultModel: ModelOption
  modelOptions: ModelOption[]
  children: React.ReactNode
  compact?: boolean
  defaultSourceViewerState?: SourceViewerState
  resetPersistedPanelContent?: boolean
}

export const ChatEnvironmentContext = createContext<
  ChatEnvironmentContextProps | undefined
>(undefined)

export const ChatEnvironmentProvider: React.FC<
  ChatEnvironmentProviderProps
> = ({
  agentName,
  orgId,
  queryKeys,
  newChatLink,
  recentChatsLink,
  promptsLink,
  getSessionLink,
  sessionId,
  pursuitId,
  children,
  compact = false,
  defaultModel = AUTO_MODEL_SELECTOR,
  modelOptions,
  defaultSourceViewerState = 'open',
  resetPersistedPanelContent = false,
}) => {
  const defaultSidebarOpen =
    sessionId === undefined
      ? DEFAULT_NEW_CHAT_SIDEBAR_OPEN
      : DEFAULT_SIDEBAR_OPEN

  // Same contract as ChatConfigurationContext below: consumers re-render on
  // value identity, so this object has to survive a parent render.
  const environment = useMemo(
    () => ({
      agentName,
      compact,
      sourceViewerState: defaultSourceViewerState,
    }),
    [agentName, compact, defaultSourceViewerState],
  )

  return (
    <ChatEnvironmentContext.Provider value={environment}>
      <ChatConfigurationProvider
        agentNameForUi={agentName}
        orgId={orgId}
        newChatLink={newChatLink}
        recentChatsLink={recentChatsLink}
        promptsLink={promptsLink}
        queryKeys={queryKeys}
        getSessionLink={getSessionLink}
        sessionId={sessionId}
        pursuitId={pursuitId}
        defaultModel={defaultModel}
        defaultSidebarOpen={defaultSidebarOpen}
        modelOptions={modelOptions}
        resetPersistedPanelContent={resetPersistedPanelContent}
        sourceViewerState={defaultSourceViewerState}
      >
        {children}
      </ChatConfigurationProvider>
    </ChatEnvironmentContext.Provider>
  )
}

interface SessionLoadingListenerProps {
  isLoading: boolean
}

export const SessionLoadingListener: React.FC<SessionLoadingListenerProps> = ({
  isLoading,
}) => {
  const store = useChatEnvironmentStore()
  useEffect(() => {
    store.setState({ isLoading })
  }, [isLoading, store])
  return null
}

export const useChatEnvironmentContext = () => {
  const context = useContext(ChatEnvironmentContext)
  if (!context) {
    throw new Error(
      'useChatEnvironmentContext must be used within an ChatEnvironmentProvider',
    )
  }
  return context
}
